import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, AppBar, Toolbar, Typography, Button, Container, Chip } from '@mui/material';
import { Dashboard } from './components/Dashboard';
import { ActiveRecall } from './components/study-techniques/ActiveRecall';
import { FeynmanTechnique } from './components/study-techniques/FeynmanTechnique';
import { Interleaving } from './components/study-techniques/Interleaving';
import { StudyStats, StudyTechnique, Flashcard as FlashcardType, UserProgress, SourceFilter } from './types';
import { SpacedRepetition } from './utils/spacedRepetition';
import {
  getOrCreateUserId,
  loadUserProgress,
  persistStudySession,
  persistUserProgress
} from './utils/progressStore';
import { isSupabaseConfigured } from './utils/supabase';

const loadFlashcardsFromFile = async (): Promise<FlashcardType[]> => {
  try {
    const response = await fetch('/flashcards.json', { cache: 'no-store' });
    if (!response.ok) {
      throw new Error('Failed to load flashcards');
    }
    const data = await response.json();
    return data.map((card: any) => ({
      ...card,
      createdAt: card.createdAt ? new Date(card.createdAt) : new Date(),
      updatedAt: card.updatedAt ? new Date(card.updatedAt) : new Date(),
    }));
  } catch (error) {
    console.error('Error loading flashcards:', error);
    return [];
  }
};

const buildFallbackFlashcards = (): FlashcardType[] => {
  const now = new Date();
  return [
    {
      id: 'sample-1',
      topic: 'Sample',
      question: 'What is FRCSORT designed to optimize?',
      answer: 'High-velocity FRCS revision using spaced repetition and targeted recall.',
      category: 'sample',
      difficulty: 'easy',
      tags: ['sample', 'frcs'],
      createdAt: now,
      updatedAt: now
    }
  ];
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0b5fff',
      contrastText: '#f8f9ff',
    },
    secondary: {
      main: '#ff7a18',
    },
    success: {
      main: '#14b86a',
    },
    warning: {
      main: '#ffb020',
    },
    background: {
      default: 'transparent',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
    }
  },
  typography: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.02em'
    },
    h2: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.02em'
    },
    h3: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.02em'
    },
    h4: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em'
    },
    h5: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600
    },
    h6: {
      fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
      fontWeight: 600
    },
    button: {
      textTransform: 'none',
      fontWeight: 600
    }
  },
  shape: {
    borderRadius: 18,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          background: 'var(--surface)',
          border: '1px solid var(--card-border)',
          boxShadow: '0 18px 40px rgba(15, 23, 42, 0.12)',
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          padding: '10px 18px',
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        }
      }
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 10,
          borderRadius: 999,
          backgroundColor: 'rgba(11, 95, 255, 0.12)'
        },
        bar: {
          borderRadius: 999
        }
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 247, 236, 0.9)',
          color: '#0f172a',
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
          backdropFilter: 'blur(16px)'
        }
      }
    }
  }
});

const buildDefaultProgress = (userId: string, cards: FlashcardType[]): UserProgress[] => (
  cards.map(card => ({
    userId,
    flashcardId: card.id,
    easeFactor: 2.5,
    interval: 1,
    repetitions: 0,
    nextReviewDate: new Date(Date.now() + 86400000),
    lastReviewed: new Date(Date.now() - 86400000),
    quality: 0,
    timesCorrect: 0,
    timesIncorrect: 0,
  }))
);

const mergeProgress = (defaults: UserProgress[], stored: UserProgress[]): UserProgress[] => {
  const progressMap = new Map(defaults.map(record => [record.flashcardId, record]));
  stored.forEach(record => {
    progressMap.set(record.flashcardId, { ...progressMap.get(record.flashcardId), ...record });
  });
  return Array.from(progressMap.values());
};

const calculateProgressByTopic = (cards: FlashcardType[], progress: UserProgress[]) => {
  const progressMap = new Map(progress.map(record => [record.flashcardId, record]));
  return cards.reduce((acc, card) => {
    const record = progressMap.get(card.id);
    if (record && record.repetitions > 0) {
      acc[card.topic] = (acc[card.topic] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
};

function App() {
  const userId = useMemo(() => getOrCreateUserId(), []);
  const [currentView, setCurrentView] = useState<'dashboard' | 'study'>('dashboard');
  const [currentTechnique, setCurrentTechnique] = useState<StudyTechnique | null>(null);
  const [flashcards, setFlashcards] = useState<FlashcardType[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [studyStats, setStudyStats] = useState<StudyStats>({
    totalCards: 0,
    cardsStudied: 0,
    averageAccuracy: 0,
    streakDays: 0,
    totalStudyTime: 0,
    techniqueUsage: {
      [StudyTechnique.ACTIVE_RECALL]: 0,
      [StudyTechnique.FEYNMAN]: 0,
      [StudyTechnique.STRUCTURAL]: 0,
      [StudyTechnique.INTERLEAVING]: 0,
      [StudyTechnique.DUAL_CODING]: 0,
    },
    progressByTopic: {},
  });

  const filteredFlashcards = useMemo(() => {
    if (sourceFilter === 'frcs') {
      return flashcards.filter(card => card.category === 'slide');
    }
    if (sourceFilter === 'journal') {
      return flashcards.filter(card => card.category === 'journal');
    }
    return flashcards;
  }, [flashcards, sourceFilter]);

  const filteredProgress = useMemo(() => {
    if (sourceFilter === 'all') return userProgress;
    const cardIds = new Set(filteredFlashcards.map(card => card.id));
    return userProgress.filter(record => cardIds.has(record.flashcardId));
  }, [filteredFlashcards, sourceFilter, userProgress]);

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      const loadedFlashcards = await loadFlashcardsFromFile();
      const resolvedFlashcards = loadedFlashcards.length > 0
        ? loadedFlashcards
        : buildFallbackFlashcards();

      if (!isMounted) return;

      setFlashcards(resolvedFlashcards);

      const defaults = buildDefaultProgress(userId, resolvedFlashcards);
      const storedProgress = await loadUserProgress(userId);

      if (!isMounted) return;

      setUserProgress(mergeProgress(defaults, storedProgress));
    };

    initializeData();
    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (!flashcards.length) return;
    setStudyStats(prev => ({
      ...prev,
      totalCards: filteredFlashcards.length,
      cardsStudied: filteredProgress.filter(record => record.repetitions > 0).length,
      averageAccuracy: SpacedRepetition.calculateRetentionRate(filteredProgress),
      progressByTopic: calculateProgressByTopic(filteredFlashcards, filteredProgress),
    }));
  }, [flashcards.length, filteredFlashcards, filteredProgress]);

  const handleStartStudy = (technique: StudyTechnique) => {
    setCurrentTechnique(technique);
    setCurrentView('study');
  };

  const handleProgressUpdate = (progress: UserProgress) => {
    setUserProgress(prev => {
      const next = prev.some(record => record.flashcardId === progress.flashcardId)
        ? prev.map(record => record.flashcardId === progress.flashcardId ? progress : record)
        : [...prev, progress];
      void persistUserProgress(userId, progress, next);
      return next;
    });
  };

  const handleSessionComplete = (sessionData: {
    technique: StudyTechnique;
    flashcardsStudied: number;
    totalTime: number;
    correctAnswers?: number;
    [key: string]: any
  }) => {
    setStudyStats(prev => ({
      ...prev,
      cardsStudied: prev.cardsStudied + sessionData.flashcardsStudied,
      totalStudyTime: prev.totalStudyTime + Math.floor(sessionData.totalTime / 60),
      techniqueUsage: {
        ...prev.techniqueUsage,
        [sessionData.technique]: prev.techniqueUsage[sessionData.technique] + 1,
      },
    }));

    void persistStudySession(userId, {
      technique: sessionData.technique,
      flashcardsStudied: sessionData.flashcardsStudied,
      correctAnswers: sessionData.correctAnswers,
      totalTime: sessionData.totalTime
    });

    setCurrentView('dashboard');
    setCurrentTechnique(null);
  };

  const renderStudyView = () => {
    if (!currentTechnique) return null;

    const dueCards = SpacedRepetition.getDueCards(filteredProgress);
    const dueFlashcards = filteredFlashcards.filter(card =>
      dueCards.some(progress => progress.flashcardId === card.id)
    );
    const newCards = filteredFlashcards
      .filter(card => !dueCards.some(progress => progress.flashcardId === card.id))
      .slice(0, Math.max(0, 10 - dueFlashcards.length));

    const studyCards = [...dueFlashcards, ...newCards];

    switch (currentTechnique) {
      case StudyTechnique.ACTIVE_RECALL:
        return (
          <ActiveRecall
            flashcards={studyCards}
            userProgress={filteredProgress}
            onProgressUpdate={handleProgressUpdate}
            onSessionComplete={handleSessionComplete}
          />
        );
      case StudyTechnique.FEYNMAN:
        return (
          <FeynmanTechnique
            flashcards={studyCards}
            userProgress={filteredProgress}
            onProgressUpdate={handleProgressUpdate}
            onSessionComplete={handleSessionComplete}
          />
        );
      case StudyTechnique.INTERLEAVING:
        return (
          <Interleaving
            flashcards={filteredFlashcards}
            userProgress={filteredProgress}
            onProgressUpdate={handleProgressUpdate}
            onSessionComplete={handleSessionComplete}
          />
        );
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4">Study Mode</Typography>
            <Typography>Technique: {currentTechnique}</Typography>
            <Button
              variant="contained"
              onClick={() => setCurrentView('dashboard')}
              sx={{ mt: 2 }}
            >
              Back to Dashboard
            </Button>
          </Box>
        );
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ minHeight: '100vh' }}>
          <AppBar position="sticky" elevation={0}>
            <Container maxWidth="lg">
              <Toolbar sx={{ px: { xs: 0 }, py: 1, gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #0b5fff 0%, #68e0ff 100%)',
                      boxShadow: '0 12px 24px rgba(11, 95, 255, 0.3)'
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      FRCSORT
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Precision FRCS learning cockpit
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                  size="small"
                  label={isSupabaseConfigured ? 'Synced' : 'Local Mode'}
                  color={isSupabaseConfigured ? 'success' : 'default'}
                  variant={isSupabaseConfigured ? 'filled' : 'outlined'}
                />
                <Button
                  color="inherit"
                  onClick={() => setCurrentView('dashboard')}
                >
                  Dashboard
                </Button>
              </Toolbar>
            </Container>
          </AppBar>

          <Container
            maxWidth={currentView === 'study' ? false : 'lg'}
            disableGutters={currentView === 'study'}
            sx={{
              py: { xs: currentView === 'study' ? 2 : 3, md: currentView === 'study' ? 3 : 5 },
              px: currentView === 'study' ? { xs: 1.5, sm: 3 } : undefined
            }}
          >
            {currentView === 'dashboard' ? (
              <Dashboard
                studyStats={studyStats}
                userProgress={filteredProgress}
                sourceFilter={sourceFilter}
                onSourceFilterChange={setSourceFilter}
                onStartStudy={handleStartStudy}
              />
            ) : (
              renderStudyView()
            )}
          </Container>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;
